import React from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Pause, Play, Square, Send } from "lucide-react";
import { toast } from "sonner";
import { StatusPill, fmtTime } from "./shared";

export default function EnrollmentsTable({ enrollments, totalSteps, onChanged }) {
  const act = async (e, action) => {
    try {
      await api.patch(`/enrollments/${e.id}`, { action });
      toast.success(`${action === "stop" ? "Stopped" : action === "pause" ? "Paused" : "Resumed"} ${e.contact_name}`);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  const sendNow = async (e) => {
    try {
      const { data } = await api.post(`/enrollments/${e.id}/send-now`);
      toast[data.failed ? "error" : "success"](data.failed ? "Send failed — check inbox for error" : data.deferred ? "Deferred: daily cap reached" : "Step sent");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="border border-border rounded-md overflow-hidden">
      <table className="cf-table w-full text-sm">
        <thead>
          <tr>
            <th className="text-left py-2 px-3 font-medium">Contact</th>
            <th className="text-left py-2 px-3 font-medium">Status</th>
            <th className="text-left py-2 px-3 font-medium">Progress</th>
            <th className="text-left py-2 px-3 font-medium">Next send</th>
            <th className="text-left py-2 px-3 font-medium">Last sent</th>
            <th className="text-right py-2 px-3 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {enrollments.map((e) => (
            <tr key={e.id} data-testid={`enrollment-row-${e.id}`}>
              <td className="py-2 px-3">
                <div className="font-medium">{e.contact_name}</div>
                <div className="text-xs mono text-muted-foreground">{e.contact_email}</div>
              </td>
              <td className="py-2 px-3"><StatusPill status={e.awaiting_approval ? "awaiting" : e.status} testId={`enrollment-status-${e.id}`} /></td>
              <td className="py-2 px-3">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-24 bg-muted rounded-sm overflow-hidden"><div className="h-full bg-primary" style={{ width: `${Math.min(100, (e.current_step / Math.max(1, totalSteps)) * 100)}%` }} /></div>
                  <span className="mono text-xs text-muted-foreground">{e.current_step}/{totalSteps}</span>
                </div>
                {e.error && <div className="text-[10px] text-rose-500 mono mt-0.5 truncate max-w-[14rem]" title={e.error}>{e.error}</div>}
              </td>
              <td className="py-2 px-3 mono text-xs text-muted-foreground">{e.awaiting_approval ? "awaiting approval" : e.status === "active" ? fmtTime(e.next_send_at) : "—"}</td>
              <td className="py-2 px-3 mono text-xs text-muted-foreground">{fmtTime(e.last_sent_at)}</td>
              <td className="py-2 px-3">
                <div className="flex justify-end gap-1">
                  {e.status === "active" && !e.awaiting_approval && <Button variant="ghost" size="icon" className="h-7 w-7" title="Send next step now" data-testid={`enrollment-send-now-${e.id}`} onClick={() => sendNow(e)}><Send className="w-3.5 h-3.5" /></Button>}
                  {e.status === "active" && <Button variant="ghost" size="icon" className="h-7 w-7" title="Pause" data-testid={`enrollment-pause-${e.id}`} onClick={() => act(e, "pause")}><Pause className="w-3.5 h-3.5" /></Button>}
                  {(e.status === "paused" || e.status === "replied") && <Button variant="ghost" size="icon" className="h-7 w-7" title="Resume" data-testid={`enrollment-resume-${e.id}`} onClick={() => act(e, "resume")}><Play className="w-3.5 h-3.5" /></Button>}
                  {(e.status === "active" || e.status === "paused") && <Button variant="ghost" size="icon" className="h-7 w-7" title="Stop" data-testid={`enrollment-stop-${e.id}`} onClick={() => act(e, "stop")}><Square className="w-3.5 h-3.5" /></Button>}
                </div>
              </td>
            </tr>
          ))}
          {enrollments.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground text-sm">No contacts enrolled yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
